import React, { useContext } from 'react'
import { ModalContext, ModalKey } from './ModalContext'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useSelector } from 'react-redux'

import ActionModal from './Popups/ActionModal'
import ActionWarningModal from './Popups/ActionWarningModal'
import AssignGroupOwnerModal from './Popups/AssignGroupOwnerModal'
import AssignRoleModal from './Popups/AssignRoleModal'
import AssignTrainerModal from './Popups/AssignTrainerModal'
import AssessmentInputModal from './Popups/AssessmentInputModal'
import AthleteOffboardModal from './Popups/AthleteOffboardModal'
import ComplianceConfigurationModal from './Popups/ComplianceConfigurationModal'
import CreateProgramModal from './Popups/CreateProgramModal'
import DeleteProgramModal from './Popups/DeleteProgramModal'
import EditAthleteMembershipModal from './Popups/EditAthleteMembershipModal'
import EditEquipmentProfileModal from './Popups/EditEquipmentProfileModal'
import EditFitnessProfileModal from './Popups/EditFitnessProfileModal'
import EditMacroVolumePhaseTypeModal from './Popups/EditMacroVolumePhaseTypeModal'
import EditOrganizationBillingModal from './Popups/EditOrganizationBillingModal'
import EventManagementModal from './Popups/EventManagementModal'
import ExerciseAssessmentInfoModal from './Popups/ExerciseAssessmentInfoModal'
import ExerciseInstructionsModal from './Popups/ExerciseInstructionsModal'
import ExerciseLibraryModal from './Popups/ExerciseLibraryModal'
import ExerciseSwapModal from './Popups/ExerciseSwapModal'
import FileImportModal from './Popups/FileImportModal'
import IncompleteAthleteRecordsModal from './Popups/IncompleteAthleteRecordsModal'
import InjuryInputModal from './Popups/InjuryInputModal'
import KinesisEventV5Modal from './Popups/KinesisEventV5Modal'
import OrganizationDirectoryModal from './Popups/OrganizationDirectoryModal'
import PlainTextEmailPreviewModal from './Popups/PlainTextEmailPreviewModal'
import ProgramLevelScoreSelectorModal from './Popups/ProgramLevelScoreSelectorModal'
import ProgramWeekInfoModal from './Popups/ProgramWeekInfoModal'
import SelectExerciseModal from './Popups/SelectExerciseModal'
import SendInviteModal from './Popups/SendInviteModal'
import SimulatorKinesisEventsModal from './Popups/SimulatorKinesisEventsModal'
import SlotExerciseProgressionDetailsModal from './Popups/SlotExerciseProgressionDetailsModal'
import TextAreaActionModal from './Popups/TextAreaActionModal'
import TextModal from './Popups/TextModal'
import TimelineColumnSettingsModal from './Popups/TimelineColumnSettingsModal'
import VideoPlayerModal from './Popups/VideoPlayerModal'
import WorkoutConfigWeekdayAssignmentOverridesModal from './Popups/WorkoutConfigWeekdayAssignmentOverridesModal'
import WorkoutConfigWeekdayAssignmentsModal from './Popups/WorkoutConfigWeekdayAssignmentsModal'
import WorkoutLengthSelectionModal from './Popups/WorkoutLengthSelectionModal'
import WorkoutOverrideModal from './Popups/WorkoutOverrideModal'
import WorkoutSummaryModal from './Popups/WorkoutSummaryModal'
import WorkoutV5CompletedSummaryModal from './Popups/WorkoutV5CompletedSummaryModal'
import WorkoutV5IncompletePreviewModal from './Popups/WorkoutV5IncompletePreviewModal'

export default function ModalOverlay() {

    const { modalData, HideModal } = useContext(ModalContext)

    const overrideOrganizationId = useSelector((state) => state.admin.overrideOrganizationId)
    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)

    const adminBannerOffsetPx = (
        (overrideOrganizationId !== null ? 44 : 0) +
        (simulationClockDayOffset !== 0 ? 52 : 0)
    )

    const HideModalWithCustomAction = () => {

        if (typeof modalData.customHideModalAction === 'function') {

            // console.log("Executing custom hide modal action!!")

            modalData.customHideModalAction()

            HideModal()

        } else {

            HideModal()

        }

    }

    if (modalData === null) return null

    if (modalData.key === ModalKey.BulkEditorOverride) return null // Display this modal inside its own container in BulkEditView.js

    return (
        <div 
            className="relative z-10" 
            onClick={() => HideModalWithCustomAction()}
        >
            {/* Background Blur */}
            {(modalData.blurBackground === undefined || modalData.blurBackground === null || modalData.blurBackground === true) && (
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    onClick={(e) => e.stopPropagation()} 
                />
            )}

            {/* Modal */}
            <div
                className="fixed inset-0 z-10 w-screen overflow-y-auto"
                style={adminBannerOffsetPx !== 0 ? { paddingTop: adminBannerOffsetPx } : undefined}
            >
                <div className={`
                    relative flex h-full p-4
                    ${modalData.size !== "snapshot-override" ? "items-end justify-center text-center sm:items-center" : ""}
                    ${modalData.size === "snapshot-override" ? "items-start justify-end" : ""}
                `}>
                    <div
                        // Size (default to half)
                        // Also add border if no background blur
                        className={`
                            relative transform overflow-auto p-4 rounded-lg bg-white shadow-xl transition-all
                            ${modalData.size === "fullscreen" ? "w-[98%] h-[98%]" : ""}
                            ${(modalData.size === "half" || modalData.size === undefined || modalData.size === null) ? "w-full max-w-3xl mx-auto space-y-4 mb-4" : ""}
                            ${modalData.size === "third" ? "w-full max-w-lg mx-auto space-y-4" : ""}
                            ${modalData.size === "quarter" ? "w-full max-w-md mx-auto space-y-4" : ""}
                            ${modalData.size === "workout-summary" ? "w-full max-w-lg mx-auto space-y-4 h-[90%] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" : ""}
                            ${modalData.size === "snapshot-override" ? "w-full max-w-md space-y-4" : ""}
                            ${modalData.blurBackground === false ? "border-2 border-gray-400" : ""}
                        `}
                        onClick={(e) => e.stopPropagation()}
                    >

                        {/* Close Button Header */}
                        <div className="flex justify-end">
                            <button 
                                className="rounded-md text-gray-400 hover:text-gray-500 -mb-4" 
                                onClick={() => HideModalWithCustomAction()}
                            >
                                <span className="sr-only">Close</span>

                                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>

                        {(() => {
                            switch (modalData.key) {
                                case ModalKey.Action: return <ActionModal />
                                case ModalKey.ActionWarning: return <ActionWarningModal />
                                case ModalKey.AssessmentInput: return <AssessmentInputModal />
                                case ModalKey.AssignGroupOwner: return <AssignGroupOwnerModal />
                                case ModalKey.AssignRole: return <AssignRoleModal />
                                case ModalKey.AssignTrainer: return <AssignTrainerModal />
                                case ModalKey.AthleteOffboard: return <AthleteOffboardModal />
                                case ModalKey.ComplianceConfiguration: return <ComplianceConfigurationModal />
                                case ModalKey.CreateProgram: return <CreateProgramModal />
                                case ModalKey.DeleteProgram: return <DeleteProgramModal />
                                case ModalKey.EditAthleteMembership: return <EditAthleteMembershipModal />
                                case ModalKey.EditEquipmentProfile: return <EditEquipmentProfileModal />
                                case ModalKey.EditFitnessProfile: return <EditFitnessProfileModal />
                                case ModalKey.EditMacroVolumePhaseType: return <EditMacroVolumePhaseTypeModal />
                                case ModalKey.EditOrganizationBilling: return <EditOrganizationBillingModal />
                                case ModalKey.EventManagement: return <EventManagementModal />
                                case ModalKey.ExerciseAssessmentInfo: return <ExerciseAssessmentInfoModal />
                                case ModalKey.ExerciseInstructions: return <ExerciseInstructionsModal />
                                case ModalKey.ExerciseLibrary: return <ExerciseLibraryModal />
                                case ModalKey.ExerciseSwap: return <ExerciseSwapModal />
                                case ModalKey.FileImport: return <FileImportModal />
                                case ModalKey.IncompleteAthleteRecords: return <IncompleteAthleteRecordsModal />
                                case ModalKey.InjuryInput: return <InjuryInputModal />
                                case ModalKey.KinesisEventV5: return <KinesisEventV5Modal />
                                case ModalKey.OrganizationDirectory: return <OrganizationDirectoryModal />
                                case ModalKey.PlainTextEmailPreview: return <PlainTextEmailPreviewModal />
                                case ModalKey.ProgramLevelScoreSelector: return <ProgramLevelScoreSelectorModal />
                                case ModalKey.ProgramWeekInfo: return <ProgramWeekInfoModal />
                                case ModalKey.SelectExercise: return <SelectExerciseModal />
                                case ModalKey.SendInvite: return <SendInviteModal />
                                case ModalKey.SimulatorKinesisEvents: return <SimulatorKinesisEventsModal />
                                case ModalKey.SlotExerciseProgressionDetails: return <SlotExerciseProgressionDetailsModal />
                                case ModalKey.Text: return <TextModal />
                                case ModalKey.TextAreaAction: return <TextAreaActionModal />
                                case ModalKey.TimelineColumnSettings: return <TimelineColumnSettingsModal />
                                case ModalKey.VideoPlayer: return <VideoPlayerModal />
                                case ModalKey.WorkoutConfigWeekdayAssignmentOverrides: return <WorkoutConfigWeekdayAssignmentOverridesModal />
                                case ModalKey.WorkoutConfigWeekdayAssignments: return <WorkoutConfigWeekdayAssignmentsModal />
                                case ModalKey.WorkoutLengthSelection: return <WorkoutLengthSelectionModal />
                                case ModalKey.WorkoutOverride: return <WorkoutOverrideModal />
                                case ModalKey.WorkoutSummary: return <WorkoutSummaryModal />
                                case ModalKey.WorkoutV5CompletedSummary: return <WorkoutV5CompletedSummaryModal />
                                case ModalKey.WorkoutV5IncompletePreview: return <WorkoutV5IncompletePreviewModal />
                                default: return <>Invalid Modal Key {modalData.key}</>
                            }
                        })()}

                    </div>

                </div>

            </div>

        </div>
    )
}
